import { useDispatchDirectives } from '@api/useDispatchDirectives'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { UploadStatusModal } from '@components/_SiteWide/UploadStatus/UploadStatusModal'
import { DateSkipper, GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { triggerGridRead } from '@utils/general'
import { Drawer, Spin } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getColumnDefs } from './columnDefs'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useUserSettings } from '@hooks/settings'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { gridViewManager } from '@/utils/grid'
import type { InferResponse } from '@/hooks/useApiClient'
import dayjs from 'dayjs'

const STORAGE_KEY = 'legacy-dispatch-directives'

type DispatchDirectiveResp = InferResponse<'/dispatch_directive/overview'>

export function OldDispatchDirective() {
  const gridReference = useRef(null)
  const [filters, setFilters] = useState({ as_of: dayjs().toISOString() })
  const [showDrawer, setShowDrawer] = useState(false)
  const [selectedRow, setSelectedRow] = useState<DispatchDirectiveResp['rows'][number]>()
  const [uploadStatusModalVisible, setUploadStatusModalVisible] = useState(false)

  const { useDispatchDirectivesQuery } = useDispatchDirectives()
  const { data: dispatchDirectives, status } = useDispatchDirectivesQuery(filters)
  const { scopes } = useSecurityContext()

  function toggleDrawer(data: DispatchDirectiveResp['rows'][number]) {
    setSelectedRow(data)
    setShowDrawer(true)
  }

  function closeDrawer() {
    setSelectedRow(undefined)
    setShowDrawer(false)
  }

  function onDayChange(date: string) {
    const sendDate = dayjs(date).toISOString()
    setFilters({
      ...filters,
      as_of: sendDate,
    })
  }
  const { updateSetting } = useUserSettings()
  const agPropOverrides = useMemo(
    () => ({
      getRowId: (row) => row.data?.key,
    }),
    []
  )
  const columnDefs = useMemo(() => getColumnDefs(toggleDrawer), [])
  return (
    <Vertical flex='1' className='pb-3'>
      <Drawer
        title='Supply History'
        placement='bottom'
        height={500}
        closable
        onClose={() => closeDrawer()}
        visible={showDrawer}
      >
        <HistoryContents selectedRow={selectedRow} />
      </Drawer>
      <div className='vertical-flex h-100'>
        <GraviGrid
          controlBarProps={{
            title: 'Legacy Directives Report',
            actionButtons: (
              <Horizontal gap={8}>
                <UploadDownloadButtons
                  refreshCallback={() => triggerGridRead(gridReference)}
                  fileName='Directives.xlsx'
                  asOfDate={filters.as_of}
                  excelOptions={[
                    { title: 'Download Directives', downloadEP: api.downloadDirectives, fileName: 'Directives.xlsx' },
                    { title: 'Upload Directives', uploadEP: scopes?.price_management?.edit && api.uploadDirectives },
                    {
                      title: 'Download Historical Template',
                      downloadEP: api.downloadHistoricalTemplate,
                      fileName: 'Historical Volumes.xlsx',
                    },
                    {
                      title: 'Upload Historical Volume',
                      uploadEP: scopes?.price_management?.edit && api.uploadHistoricalVolume,
                    },
                  ]}
                />
                <Horizontal verticalCenter>
                  <DateSkipper
                    startDate={filters.as_of}
                    daysToSkip={1}
                    onChange={onDayChange}
                    format={formatters.DATE}
                  />
                </Horizontal>
              </Horizontal>
            ),
          }}
          className='dispatch-directive-grid vertical-flex'
          loading={status === 'pending'}
          agPropOverrides={agPropOverrides}
          columnDefs={columnDefs}
          storageKey={STORAGE_KEY}
          rowData={dispatchDirectives?.rows}
          onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
          {...gridViewManager(STORAGE_KEY, updateSetting)}
        />
      </div>

      <UploadStatusModal
        uploadStatusModalVisible={uploadStatusModalVisible}
        setUploadStatusModalVisible={setUploadStatusModalVisible}
      />
    </Vertical>
  )
}

type DirectiveHistoryResp = InferResponse<'/dispatch_directive/history'>

function HistoryContents({ selectedRow }: { selectedRow?: DispatchDirectiveResp['rows'][number] }) {
  if (!selectedRow) return null

  const [loading, setLoading] = useState(true)
  const [history, setHistory] = useState<DirectiveHistoryResp>()
  const curve_group_id = selectedRow.key
  const as_of = selectedRow.directive_effective_from
  const columnDefs = getColumnDefs(null).splice(1) // Remove the first column which is not needed here
  const { updateSetting } = useUserSettings()
  useEffect(() => {
    api.getDispatchDirectoryHistory(curve_group_id, as_of).then(({ data }) => {
      setHistory(data)
      setLoading(false)
    })
  }, [selectedRow.curve_group_id])

  if (loading)
    return (
      <div className='center-flex-container'>
        <Spin />
      </div>
    )

  return (
    <Vertical>
      <Horizontal justifyContent='space-between' className='price-history-header py-2 px-3 mb-2' verticalCenter>
        <Horizontal alignItems='flex-end'>
          <Texto category='h4' className='mr-1' weight={'bold'}>
            {selectedRow.product}
          </Texto>
          <Texto category='h5'>{selectedRow.terminal && `- ${selectedRow.terminal}`}</Texto>
        </Horizontal>
        <Horizontal alignItems='flex-end'>
          <Texto category='h4' className='mr-1' weight={'bold'}>
            {selectedRow.supplier}
          </Texto>
          <Texto category='h5'>{selectedRow.price_type && `- ${selectedRow.price_type.toUpperCase()}`}</Texto>
        </Horizontal>
      </Horizontal>
      <GraviGrid
        className='dispatch-directive-grid vertical-flex'
        agPropOverrides={{
          columnDefs,
          getRowId: (row) => row.data?.directive_effective_from,
        }}
        storageKey='legacy-dispatch-directives-individual'
        rowData={history}
        onGridConfigChanged={(config) => updateSetting('gridConfig::legacy-dispatch-directives-individual', config)}
      />
    </Vertical>
  )
}
